// Host-side audio streamer for the browser P2P mesh.
//
// Wraps a MediaStream (the output of getDisplayMedia + audio):
//
//   MediaStream → AudioContext (48 kHz)
//     ↓ createMediaStreamSource
//   capture-worklet  (accumulates 20 ms interleaved-stereo f32 frames)
//     ↓ port.postMessage (zero-copy ArrayBuffer)
//   main thread
//     ↓ frame with deterministic play_at_us = base + index × 20 ms
//   PeerHub.broadcast → every joiner's DataChannel
//
// The play_at base is anchored relative to performance.now() (the
// joiner's NTP-style probes map this to their own performance clock).
// Frame index advances on EVERY worklet message, so even if a single
// frame goes missing for some reason, subsequent frames still land
// exactly 20 ms apart on the receiver's anchor grid.

import { encodeAudioPcm, encodePong, decode } from './wire'
import type { MeshLink, PeerEvent } from './PeerHub'
import { ClockSync } from '@/audio/clockSync'
import { Scheduler } from '@/audio/scheduler'

/** Server-side "target_ahead" cushion. The deterministic play_at for
 *  frame N is set so the audio lands this many µs after the host saw
 *  the capture, giving the network + jitter buffers room to breathe.
 *  Match the Rust pipeline's default. */
const TARGET_AHEAD_US = 240_000n
const FRAME_DURATION_US = 20_000n

export interface HostStreamerOptions {
  /** Pull in MediaStream tracks from this stream (typically from
   *  getDisplayMedia or getUserMedia). */
  stream?: MediaStream
  audioElement?: HTMLMediaElement
  localPlayback?: boolean
  hub: MeshLink
}

export class HostStreamer {
  private ctx: AudioContext | null = null
  private srcNode: MediaStreamAudioSourceNode | MediaElementAudioSourceNode | null = null
  private workletNode: AudioWorkletNode | null = null
  private silentSink: GainNode | null = null
  private hub: MeshLink
  private stream?: MediaStream
  private audioElement?: HTMLMediaElement
  private localPlayback = false
  private peerListener: (e: Event) => void

  private playAtBaseUs: bigint | null = null
  private frameIndex = 0
  private loggedFirstFrame = false

  private localScheduler: Scheduler | null = null
  private localClock: ClockSync | null = null

  constructor(opts: HostStreamerOptions) {
    this.hub = opts.hub
    this.stream = opts.stream
    this.audioElement = opts.audioElement
    this.localPlayback = !!opts.localPlayback
    this.peerListener = (e: Event) => {
      const ev = (e as CustomEvent<PeerEvent>).detail
      if (ev.type === 'data') this.handleInbound(ev.peerId, ev.data)
    }
  }

  setLocalVolume(v: number) {
    this.localScheduler?.setVolume(v)
  }

  setLocalMuted(muted: boolean) {
    this.localScheduler?.setMuted(muted)
  }

  /** Start capture + broadcast. Resolves once the AudioContext +
   *  worklet are loaded; samples start flowing on the next
   *  process() tick. */
  async start(): Promise<void> {
    if ('audioSession' in navigator) {
      try {
        (navigator as any).audioSession.type = 'playback'
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[Multispeaker host] Failed to set audioSession.type to playback:', e)
      }
    }
    this.ctx = new AudioContext({ sampleRate: 48_000, latencyHint: 'interactive' })
    await this.ctx.audioWorklet.addModule('/capture-worklet.js')

    if (this.stream) {
      const audioTracks = this.stream.getAudioTracks()
      if (audioTracks.length === 0) {
        throw new Error('MediaStream has no audio track to capture')
      }
      const audioStream = new MediaStream(audioTracks)
      this.srcNode = this.ctx.createMediaStreamSource(audioStream)
    } else if (this.audioElement) {
      this.srcNode = this.ctx.createMediaElementSource(this.audioElement)
    } else {
      throw new Error('No audio source provided')
    }

    this.workletNode = new AudioWorkletNode(this.ctx, 'capture', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    })

    // The worklet must be reachable from `destination` so its
    // process() is scheduled. Route through a silent gain so we
    // don't leak captured audio back through the host's speakers.
    this.silentSink = this.ctx.createGain()
    this.silentSink.gain.value = 0
    this.srcNode.connect(this.workletNode)
    this.workletNode.connect(this.silentSink).connect(this.ctx.destination)

    if (this.localPlayback) {
      this.localClock = new ClockSync()
      // Seed with a 0n theta probe (host is in host performance time)
      this.localClock.addProbe(0n, 0n, 0n, 0n)
      this.localScheduler = new Scheduler(this.ctx, this.localClock)
    }

    this.workletNode.port.onmessage = (ev) => this.onWorkletMessage(ev)

    this.hub.addEventListener('peer', this.peerListener)

    // eslint-disable-next-line no-console
    console.info(
      `[Multispeaker host] ctx state=${this.ctx.state}, sampleRate=${this.ctx.sampleRate}` +
      (this.stream ? `, audio track readyState=${this.stream.getAudioTracks()[0].readyState}` : '')
    )

    // Chrome creates AudioContexts in 'suspended' state. Without an
    // explicit resume() the worklet's process() never fires and no
    // frames flow. The caller is inside a user-gesture handler
    // (clicked "Share screen audio" or "Play local file"), so this is allowed.
    if (this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume()
        // eslint-disable-next-line no-console
        console.info(`[Multispeaker host] ctx resumed, state=${this.ctx.state}`)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[Multispeaker host] ctx.resume() rejected', e)
      }
    }

    // Sanity heartbeat — if the worklet never posts a frame, this
    // log tells us whether the AudioContext is even running.
    setTimeout(() => {
      if (this.loggedFirstFrame) return
      // eslint-disable-next-line no-console
      console.warn(
        `[Multispeaker host] no frames after 2 s — ctx.state=${this.ctx?.state}, ctx.currentTime=${this.ctx?.currentTime}, peers=${this.hub.peerIds().length}`,
      )
    }, 2_000)
  }

  stop(): void {
    this.hub.removeEventListener('peer', this.peerListener)
    try { this.workletNode?.disconnect() } catch { /* noop */ }
    try { this.silentSink?.disconnect() } catch { /* noop */ }
    try { this.srcNode?.disconnect() } catch { /* noop */ }
    this.localScheduler?.destroy()
    this.localScheduler = null
    this.localClock = null
    // Don't stop the MediaStream tracks — the caller owns the lifetime.
    void this.ctx?.close()
    this.ctx = null
    this.workletNode = null
    this.silentSink = null
    this.srcNode = null
  }

  private onWorkletMessage(ev: MessageEvent) {
    if (!(ev.data instanceof ArrayBuffer)) {
      const msg = ev.data as { type?: string }
      if (msg?.type === 'capture-ready') {
        // eslint-disable-next-line no-console
        console.info('[Multispeaker host] capture worklet process() is running')
      } else if (msg?.type === 'audible') {
        // eslint-disable-next-line no-console
        console.info('[Multispeaker host] capture worklet sees non-zero samples')
      }
      return
    }
    if (this.playAtBaseUs === null) {
      this.playAtBaseUs =
        BigInt(Math.round(performance.now() * 1000)) + TARGET_AHEAD_US
    }
    const playAtUs =
      this.playAtBaseUs + BigInt(this.frameIndex) * FRAME_DURATION_US
    const seq = this.frameIndex >>> 0
    this.frameIndex++

    const samples = new Float32Array(ev.data)
    const frame = encodeAudioPcm(playAtUs, seq, samples)
    this.hub.broadcast(frame)

    if (this.localScheduler) {
      this.localScheduler.ingest({
        seq,
        play_at_us: playAtUs,
        samples: samples,
      })
    }

    if (!this.loggedFirstFrame) {
      this.loggedFirstFrame = true
      // eslint-disable-next-line no-console
      console.info(
        `[Multispeaker host] first audio frame broadcast (${frame.byteLength} B, ${this.hub.peerIds().length} peer(s))`,
      )
    }
  }

  /** Reply to NTP-style PING probes from joiners. Anything else
   *  inbound is ignored. */
  private handleInbound(peerId: string, data: ArrayBuffer) {
    const decoded = decode(data)
    if (decoded.kind !== 'ping') return
    const hostRecvUs = BigInt(Math.round(performance.now() * 1000))
    // Negligible processing — capture send timestamp tightly.
    const hostSendUs = BigInt(Math.round(performance.now() * 1000))
    const pong = encodePong(decoded.clientSendUs, hostRecvUs, hostSendUs)
    this.hub.sendTo(peerId, pong)
  }
}
